import {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { QUOTE_IMAGES_COLLECTION_QUERY } from "../../Services/Queries/Queries";
import { setNotification } from "../../../../helpers/cache";
import { DataCollectionItem } from "../../../../types/AdminDashboard";
import {
  CREATE_QUOTE_IMAGE,
  DELETE_QUOTE_IMAGE,
  UPDATE_QUOTE_IMAGE,
  UPDATE_QUOTE_IMAGE_IS_FOR_SUPPLIER,
} from "../../Services/Mutations/Mutations";
import QuoteDeleteModal from "./QuoteDeleteModal1";
import { useDisableBodyScroll } from "../../../../helpers";
import { ChildrenTable, TableHeading } from "../../../../types/Table";
import QuoteFileIcon from "../../../../helpers/QuoteFileIcon";
import CheckboxNew from "../../../../components/Form/El/CheckboxNew";
import TableWithHeaderNew from "../../../../components/Table/TableWithHeaderNew";
import EditQuoteInputFiles from "./EditFilesInputFiles";
import { Base64toObject, fileToBase64Promise } from "../../../../helpers/utils";
import SelectFromServerData from "../../../../components/Form/El/SelectFromServerData";
import Input from "../../../../components/Form/El/Input";
import { cloneDeep } from "lodash";

const EditQuoteArtWork: FC<{
  quoteId: number;
  accessModulesTabsArtWork: any;
  accessControlStructure: any;
  hasEditAndDeleteAccess: boolean;
  userId?: number;
  isForSupplierForm?: boolean;
}> = ({
  quoteId,
  accessModulesTabsArtWork,
  accessControlStructure,
  hasEditAndDeleteAccess,
  userId,
  isForSupplierForm = false,
}) => {
  const [getArtworkFiles] = useLazyQuery(QUOTE_IMAGES_COLLECTION_QUERY, {
    fetchPolicy: "no-cache",
    onError: (error) => {
      setNotification([{ type: "ERROR", message: error?.message }]);
    },
  });

  const [deleteQuoteImage] = useMutation(DELETE_QUOTE_IMAGE, {
    onError: (error) => {
      setNotification([{ type: "ERROR", message: error?.message }]);
    },
    onCompleted: (data) => {
      setIsNeedUpdateFiles(true);

      setNotification([
        {
          type: "SUCCESS",
          message: "File deleted successfully",
        },
      ]);
    },
  });

  const [updateIsForSupplier] = useMutation(
    UPDATE_QUOTE_IMAGE_IS_FOR_SUPPLIER,
    {
      onError: (error) => {
        setNotification([{ type: "ERROR", message: error?.message }]);
      },
      onCompleted: (data) => {
        setIsNeedUpdateFiles(true);

        setNotification([
          {
            type: "SUCCESS",
            message: "File has been updated successfully",
          },
        ]);
      },
    }
  );

  const [updateQuoteImage] = useMutation(UPDATE_QUOTE_IMAGE, {
    onError: (error) => {
      setNotification([{ type: "ERROR", message: error?.message }]);
    },
    onCompleted: (data) => {
      setIsNeedUpdateFiles(true);

      setNotification([
        {
          type: "SUCCESS",
          message: "File updated successfully",
        },
      ]);
    },
  });
  const [createQuoteImage] = useMutation(CREATE_QUOTE_IMAGE, {
    onError: (error) => {
      setNotification([{ type: "ERROR", message: error?.message }]);
    },
    onCompleted: (data) => {
      setNotification([
        {
          type: "SUCCESS",
          message: "File created successfully",
        },
      ]);
    },
  });

  const accessModulesData = useMemo(
    () => accessModulesTabsArtWork?.data,
    [accessModulesTabsArtWork]
  );
  const accessTabsData = useMemo(
    () => accessModulesData?.tabs,
    [accessModulesData]
  );
  const accessLogoFiles = useMemo(
    () => accessTabsData?.logoFiles,
    [accessTabsData]
  );
  const accessSampleFiles = useMemo(
    () => accessTabsData?.sampleFiles,
    [accessTabsData]
  );
  const accessFinalProofFiles = useMemo(
    () => accessTabsData?.finalProof,
    [accessTabsData]
  );

  const getAccessEditByType = useCallback(
    (type: number) => {
      switch (type) {
        case 0:
          return accessLogoFiles;
        case 1:
          return accessSampleFiles;
        case 2:
          return accessFinalProofFiles;
      }
    },
    [accessLogoFiles, accessSampleFiles, accessFinalProofFiles]
  );

  const getAccessButtonsByType = useCallback((type: number) => {
    const accesses = getAccessEditByType(type);

    return accesses ? accesses?.data?.buttons : [];
  }, [getAccessEditByType]);

  const [isLoadingUpdateArtworkFiles, setIsLoadingUpdateArtworkFiles] =
    useState<number | null>(null);
  const [isLoadingCreateArtworkFiles, setIsLoadingCreateArtworkFiles] =
    useState(false);
  const [isLoadingArtworkFiles, setIsLoadingArtworkFiles] = useState(false);
  const [isLoadingDeleteArtworkFiles, setIsLoadingDeleteArtworkFiles] =
    useState(false);
  const [isNeedUpdateFiles, setIsNeedUpdateFiles] = useState(true);
  const [dataCollection, setDataCollection] = useState<DataCollectionItem[]>(
    []
  );
  const [deleteIds, setDeleteIds] = useState<number[]>([]);
  const [selectedArtworkIds, setSelectedArtworkIds] = useState<number[]>([]);
  const [imageType, setImageType] = useState(0);
  const [commentsImage, setCommentsImage] = useState<
    { id: number; value: string }[]
  >([]);

  const onSetNewCommentImage = useCallback(
    (id: number, value: string) => {
      const tmpCommentsImage = cloneDeep(commentsImage);
      const index = tmpCommentsImage.findIndex((el) => el.id === id);

      if (index === -1) {
        tmpCommentsImage.push({ id, value });
      }

      tmpCommentsImage[index] = { id, value };

      setCommentsImage(tmpCommentsImage);
    },
    [commentsImage]
  );

  const onGetArtworkFiles = useCallback(async () => {
    setIsLoadingArtworkFiles(true);
    await getArtworkFiles({
      variables: {
        quoteId,
      },
    })
      .then((res) => {
        const { data } = res;
        if (!data) {
          setDataCollection([]);
          return;
        }

        const { quoteImagesByQuote } = data;

        if (!quoteImagesByQuote) {
          setDataCollection([]);
          return;
        }

        setDataCollection(quoteImagesByQuote);
        setCommentsImage([]);
      })
      .finally(() => {
        setIsLoadingArtworkFiles(false);

        if (isNeedUpdateFiles) {
          setIsNeedUpdateFiles(false);
        }
      });
  }, [quoteId]);

  const onDeleteArtworkFiles = useCallback(async () => {
    if (!deleteIds.length) return;

    setIsLoadingDeleteArtworkFiles(true);

    await deleteQuoteImage({
      variables: {
        ids: deleteIds,
      },
    }).finally(() => {
      setIsLoadingDeleteArtworkFiles(false);
      setDeleteIds([]);
      setSelectedArtworkIds((prevList) =>
        prevList.filter((id) => !deleteIds.includes(id))
      );
    });
  }, [deleteIds, deleteQuoteImage]);

  const onUpdateIsForSuppliers = async (id: number, isForSupplier: boolean) => {
    setIsLoadingArtworkFiles(() => true);
    await updateIsForSupplier({
      variables: { id, isForSupplier },
    });
    setIsLoadingArtworkFiles(() => false);
  };

  const forceDownload = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    url: string,
    fileName: string
  ) => {
    e.preventDefault();

    try {
      const response = await fetch(url, {
        mode: "cors",
        cache: "no-cache",
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const urlCreator = window.URL || window.webkitURL;
      const imageUrl = urlCreator.createObjectURL(blob);
      const tag = document.createElement("a");
      tag.href = imageUrl;
      tag.download = fileName;
      document.body.appendChild(tag);
      tag.click();
      document.body.removeChild(tag);
      urlCreator.revokeObjectURL(imageUrl);
    } catch (error) {
      console.error("Download failed:", error);
      setNotification([{ type: "ERROR", message: "Failed to download file" }]);
    }
  };

  // const forceDownload = (
  //   e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  //   url: string,
  //   fileName: string,
  // ) => {
  //   e.preventDefault();
  //   var xhr = new XMLHttpRequest();
  //   xhr.open("GET", url, true);
  //   xhr.responseType = "blob";
  //   xhr.onload = function () {
  //     var urlCreator = window.URL || window.webkitURL;
  //     var imageUrl = urlCreator.createObjectURL(this.response);
  //     var tag = document.createElement("a");
  //     tag.href = imageUrl;
  //     tag.download = fileName;
  //     document.body.appendChild(tag);
  //     tag.click();
  //     document.body.removeChild(tag);
  //   };
  //   xhr.send();
  // };

  useEffect(() => {
    if (
      quoteId &&
      isNeedUpdateFiles &&
      accessLogoFiles &&
      accessSampleFiles &&
      accessFinalProofFiles
    ) {
      onGetArtworkFiles();
    }
  }, [
    isNeedUpdateFiles,
    quoteId,
    accessLogoFiles,
    accessSampleFiles,
    accessFinalProofFiles,
  ]);

  const onUpdateArtworkFile = useCallback(
    async (
      id: number,
      variables: Record<string, string | number | boolean>
    ) => {
      setIsLoadingUpdateArtworkFiles(id);

      await updateQuoteImage({
        variables: {
          id,
          ...variables,
        },
      }).finally(() => {
        setIsLoadingUpdateArtworkFiles(null);
      });
    },
    []
  );

  const canDeleteArtworkFiles = useMemo(() => {
    return (
      hasEditAndDeleteAccess &&
      (getAccessButtonsByType(0)?.includes("delete") ||
        getAccessButtonsByType(1)?.includes("delete") ||
        getAccessButtonsByType(2)?.includes("delete"))
    );
  }, [hasEditAndDeleteAccess, getAccessButtonsByType]);

  const onToggleSelectedArtworkId = useCallback(
    (id: number, value: boolean) => {
      setSelectedArtworkIds((prevList) => {
        if (value) {
          return prevList.includes(id)
            ? prevList
            : [...prevList, id].sort((a, b) => a - b);
        }

        return prevList.filter((selectedId) => selectedId !== id);
      });
    },
    []
  );

  useEffect(() => {
    if (!dataCollection.length) {
      setSelectedArtworkIds([]);
      return;
    }

    const availableIds = dataCollection.map((el) => el.id);
    setSelectedArtworkIds((prevList) =>
      prevList.filter((id) => availableIds.includes(id))
    );
  }, [dataCollection]);

  const tableHeading = useMemo((): TableHeading => {
    let currentTableHeading: TableHeading = [
      ...(canDeleteArtworkFiles
        ? [
            {
              name: "select",
              label: "",
            },
          ]
        : []),
      {
        name: "image",
        label: "Image",
      },
      {
        name: "description",
        label: "Description",
      },
      {
        name: "status",
        label: "",
      },
      ...(isForSupplierForm
        ? [
            {
              name: "isForSuppliers",
              label: "for Suppliers",
            },
          ]
        : []),
    ];

    if (
      canDeleteArtworkFiles
    ) {
      currentTableHeading.push({
        name: "delete",
        label: "",
      });
    }

    return currentTableHeading;
  }, [canDeleteArtworkFiles]);

  const childrenForTable = useCallback(
    (
      type: number,
      currentDataCollection: DataCollectionItem[]
    ): ChildrenTable[] => {
      let currentChildrenForTable: ChildrenTable[] = [];

      const getImageTypeLabelByNumber = (id: number) => {
        if (id === 0) {
          return "Logo Files";
        }

        if (id === 1) {
          return "Samples";
        }

        if (id === 2) {
          return "Final Proof";
        }

        return "-";
      };

      currentDataCollection
        .filter((el) => el.imageType === type)
        .forEach((dataCollectionItem) => {
          const {
            id,
            imageName,
            imageURL,
            imageType,
            imageFile,
            comment,
            approved,
            imageUid,
            isForSupplier,
          } = dataCollectionItem;

          let fileUrl = "";

          if (imageURL && !imageURL.toLowerCase().includes("file missing")) {
            fileUrl = imageURL;
          }
          if (!fileUrl && imageUid) {
            fileUrl = imageUid;
          }

          let fileFromBase64ToFile = "";

          if (imageFile) {
            fileFromBase64ToFile = Base64toObject(imageFile);
          }

          const accessButtonsObj = getAccessButtonsByType(type);

          let currentChildrenForTableItem: ChildrenTable = {
            id,
            row: {
              select: canDeleteArtworkFiles ? (
                <div className="flex justify-center">
                  {accessButtonsObj?.includes("delete") ? (
                    <CheckboxNew
                      value={selectedArtworkIds.includes(id)}
                      onChange={(value) => {
                        onToggleSelectedArtworkId(id, value);
                      }}
                      name={`artwork-file-select-checkbox-${id}`}
                      disabled={isLoadingDeleteArtworkFiles}
                    />
                  ) : null}
                </div>
              ) : null,
              image: (
                <div className="flex items-center justify-center w-full">
                  <div className="w-24 p-3 object-center">
                    <QuoteFileIcon imageName={imageName} imageURL={fileUrl} />
                  </div>
                </div>
              ),
              description: (
                <Fragment>
                  <div>
                    <span className="font-bold">Name: </span>
                    <span>
                      {!!imageName && (!!fileUrl || !!fileFromBase64ToFile) && (
                        <>
                          <a
                            href={fileUrl || fileFromBase64ToFile}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {imageName}
                          </a>{" "}
                          (
                          <a
                            href={fileUrl || fileFromBase64ToFile}
                            onClick={(e) =>
                              forceDownload(e, fileUrl, imageName)
                            }
                          >
                            Download
                          </a>
                          )
                        </>
                      )}
                      {!!imageName && !fileUrl && !fileFromBase64ToFile && (
                        <Fragment>{imageName}</Fragment>
                      )}
                      {!imageName && "-"}
                    </span>
                  </div>
                  <div>
                    <span className="font-bold">Type: </span>
                    <span>{getImageTypeLabelByNumber(imageType) || "-"}</span>
                  </div>
                  <div>
                    <span className="font-bold">Comment: </span>
                    <span>{comment || "-"}</span>
                  </div>
                </Fragment>
              ),
              status: (
                <div
                  className={`text-[13px] font-bold ${
                    approved ? "" : "text-[#af3013]"
                  }`}
                >
                  {approved ? "Approved" : "Disapproved"}
                </div>
              ),
              isForSuppliers: (
                <div className="flex justify-center">
                  <input
                    type="checkbox"
                    checked={!!isForSupplier}
                    onChange={() => onUpdateIsForSuppliers(id, !isForSupplier)}
                  />
                </div>
              ),
            },
          };

          if (accessButtonsObj?.includes("delete")) {
            currentChildrenForTableItem.row.delete = (
              <button
                className="btn btn-warning btn-mini"
                onClick={() => {
                  setDeleteIds([id]);
                }}
              >
                <i
                  className={`${
                    deleteIds.includes(id)
                      ? "inline-block animate-spin icon-spin4"
                      : "icon-trash-empty"
                  } icon-medium`}
                />
              </button>
            );
          }

          const getImageComment = (id: number) => {
            const currentCommentObj = commentsImage.find((el) => el.id === id);

            return currentCommentObj?.value || "";
          };

          const imageComment = getImageComment(id);

          if (
            hasEditAndDeleteAccess &&
            (accessButtonsObj?.includes("checkbox") ||
              accessButtonsObj?.includes("edit"))
          ) {
            currentChildrenForTableItem.expandRow = (
              <div className="flex flex-col gap-1">
                {accessButtonsObj?.includes("checkbox") && (
                  <CheckboxNew
                    value={approved}
                    onChange={(value) => {
                      onUpdateArtworkFile(id, { approved: value });
                    }}
                    name={`artwork-file-approved-checkbox-${id}`}
                    label="Approved: "
                    disabled={isLoadingUpdateArtworkFiles === id}
                  />
                )}
                {accessButtonsObj?.includes("edit") && (
                  <Fragment>
                    <Input
                      value={imageComment}
                      onChange={(value) => {
                        onSetNewCommentImage(id, value.toString());
                      }}
                      name={`comment-image-${id}`}
                      inputWidth="200px"
                    />
                    <div className="btn-group">
                      <button
                        className="btn btn-success btn-small"
                        onClick={() => {
                          onUpdateArtworkFile(id, {
                            comment: imageComment || "",
                          });
                        }}
                      >
                        <i
                          className={`${
                            isLoadingUpdateArtworkFiles === id
                              ? "inline-block animate-spin icon-spin4"
                              : "icon-floppy"
                          } pr-1`}
                        />
                        Save comment
                      </button>
                    </div>
                  </Fragment>
                )}
              </div>
            );
          }

          currentChildrenForTable.push(currentChildrenForTableItem);
        });

      return currentChildrenForTable;
    },
    [
      hasEditAndDeleteAccess,
      deleteIds,
      commentsImage,
      canDeleteArtworkFiles,
      selectedArtworkIds,
      isLoadingDeleteArtworkFiles,
      onToggleSelectedArtworkId,
      getAccessButtonsByType,
      onSetNewCommentImage,
      onUpdateArtworkFile,
    ]
  );

  const logoChildrenForTable = useMemo(
    () => childrenForTable(0, dataCollection),
    [childrenForTable, dataCollection]
  );
  const samplesChildrenForTable = useMemo(
    () => childrenForTable(1, dataCollection),
    [childrenForTable, dataCollection]
  );
  const finalProofChildrenForTable = useMemo(
    () => childrenForTable(2, dataCollection),
    [childrenForTable, dataCollection]
  );

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  useDisableBodyScroll(showDeleteModal);
  useEffect(() => {
    setShowDeleteModal(!!deleteIds.length);
  }, [deleteIds]);

  const [fileComment, setFileComment] = useState("");
  const [filesList, setFilesList] = useState<File[]>([]);
  const [filesVisForSuppliers, setFilesVisForSuppliers] = useState<number[]>(
    []
  );

  // -- Set visibility for new files
  const fileListLen = useRef<number>(0);
  useEffect(() => {
    if (filesList.length > fileListLen.current) {
      const newFileIndxs = [
        ...Array(filesList.length - fileListLen.current),
      ].map((_, i) => filesList.length - i - 1);
      setFilesVisForSuppliers((prevList) => [...prevList, ...newFileIndxs]);
    }

    fileListLen.current = filesList.length;
  }, [filesList.length]);

  const onCreateFiles = useCallback(async () => {
    const newFilesList: {
      imageName: string;
      imageFile: string;
    }[] = [];

    setIsLoadingCreateArtworkFiles(true);

    try {
      for (const file of filesList) {
        if (file.size > 1e9) {
          alert(
            `File with name ${file.name} more than 1GB and will not be added`
          );
          return;
        }

        const result = await fileToBase64Promise(file);
        if (typeof result === "string") {
          newFilesList.push({
            imageName: file.name,
            imageFile: result,
          });
        }
      }

      await createQuoteImage({
        variables: {
          quoteId: quoteId,
          comment: fileComment,
          imageFiles: newFilesList,
          filesVisForSuppliers,
          userId,
          imageType,
        },
      }).then((res) => {
        const { data } = res;

        if (!data) return;

        if (data.createQuoteImage) {
          setFilesList([]);
          setIsNeedUpdateFiles(true);
          setNotification([
            {
              type: "SUCCESS",
              message: "Files successfully saved",
            },
          ]);
        }
      });
    } catch (error) {
      setNotification([
        {
          type: "ERROR",
          message: `Failed to read file: ${
            (error as Error)?.message || "unknown error"
          }`,
        },
      ]);
    } finally {
      setIsLoadingCreateArtworkFiles(false);
    }
  }, [filesList, quoteId, filesVisForSuppliers, imageType]);

  return (
    <section>
      {canDeleteArtworkFiles && (
        <div className="flex items-center justify-end gap-2 mb-2">
          {!!selectedArtworkIds.length && (
            <button
              className="btn btn-small"
              onClick={() => {
                setSelectedArtworkIds([]);
              }}
              disabled={isLoadingDeleteArtworkFiles}
            >
              Clear Selection
            </button>
          )}
          <button
            className="btn btn-small btn-warning"
            onClick={() => {
              setDeleteIds(selectedArtworkIds);
            }}
            disabled={
              isLoadingDeleteArtworkFiles || !selectedArtworkIds.length
            }
          >
            <i
              className={`${
                isLoadingDeleteArtworkFiles
                  ? "inline-block animate-spin icon-spin4"
                  : "icon-trash-empty"
              } pr-1`}
            />
            Delete Selected
            {!!selectedArtworkIds.length && ` (${selectedArtworkIds.length})`}
          </button>
        </div>
      )}
      <TableWithHeaderNew
        heading={tableHeading}
        setIsNeedUpdateTableData={() => {
          setIsNeedUpdateFiles(true);
        }}
        children={logoChildrenForTable}
        countPages={logoChildrenForTable.length ? 1 : 0}
        header="Logo Files"
        showTryAgainMenu={false}
        loading={isLoadingArtworkFiles}
      />
      <TableWithHeaderNew
        heading={tableHeading}
        setIsNeedUpdateTableData={() => {
          setIsNeedUpdateFiles(true);
        }}
        children={samplesChildrenForTable}
        countPages={samplesChildrenForTable.length ? 1 : 0}
        header="Samples Files"
        showTryAgainMenu={false}
        loading={isLoadingArtworkFiles}
      />
      <TableWithHeaderNew
        heading={tableHeading}
        setIsNeedUpdateTableData={() => {
          setIsNeedUpdateFiles(true);
        }}
        children={finalProofChildrenForTable}
        countPages={finalProofChildrenForTable.length ? 1 : 0}
        header="Final Proof Files"
        showTryAgainMenu={false}
        loading={isLoadingArtworkFiles}
      />
      {!!hasEditAndDeleteAccess && (
        <Fragment>
          <div className="text-xl text-[#393939]">Upload new image</div>
          <SelectFromServerData
            value={imageType}
            onChange={(value) => {
              if (typeof value === "undefined") return;
              let currentValue = value;

              if (typeof currentValue === "string") {
                currentValue = +currentValue;
              }

              setImageType(currentValue);
            }}
            type="quoteImageTypes"
            selectWidth="350px"
          />
          <EditQuoteInputFiles
            onChange={(newFiles) => {
              setFilesList(newFiles);
            }}
            fileComment={{
              value: fileComment,
              onChange: (value) => {
                setFileComment(value);
              },
            }}
            filesVisForSuppliers={
              isForSupplierForm
                ? {
                    value: filesVisForSuppliers,
                    onChange: (value: typeof filesVisForSuppliers) => {
                      setFilesVisForSuppliers(value);
                    },
                  }
                : undefined
            }
            filesList={filesList}
          />
          <div className="btn-group">
            <button
              className="btn btn-small btn-success"
              onClick={() => {
                onCreateFiles();
              }}
              disabled={isLoadingCreateArtworkFiles || !filesList.length}
            >
              <i
                className={`${
                  isLoadingCreateArtworkFiles
                    ? "inline-block animate-spin icon-spin4"
                    : "icon-floppy"
                } pr-1`}
              />
              Add File
            </button>
          </div>
        </Fragment>
      )}
      {showDeleteModal && (
        <QuoteDeleteModal
          setShowDeleteModal={() => {
            setDeleteIds([]);
          }}
          deleteTitle={
            deleteIds.length > 1
              ? "Delete selected artwork files"
              : "Delete logo image"
          }
          onDelete={onDeleteArtworkFiles}
          loading={isLoadingDeleteArtworkFiles}
        />
      )}
    </section>
  );
};

export default EditQuoteArtWork;
